import { FormError as IrisFormError } from "@eworkit/iris";
import { isNumber, isString, isUndefined } from "lodash-es";
import React from "react";
import { useTranslation } from "react-i18next";

import { VALIDATION_TYPES } from "@/components/Formik";
import { IErrorField } from "@/core/api";

import { OverrideMessages } from "./OverrideMessages";

export interface IErrorFieldTextProps {
  error?: string | IErrorField;
  overrideMessages?: OverrideMessages;
}

const DEFAULT_ERROR_KEY = VALIDATION_TYPES.GENERAL;

const withTranslationNamespace = (key: string) => `VALIDATION_MESSAGES.${key || DEFAULT_ERROR_KEY}`;

const findKey = (key: string, overrideMessages?: OverrideMessages) => {
  if (overrideMessages && overrideMessages[key]) {
    return overrideMessages && overrideMessages[key];
  }

  return withTranslationNamespace(key);
};

const getMessage = (error?: string | IErrorField, overrideMessages?: OverrideMessages) => {
  if (isUndefined(error)) {
    return "";
  }

  if (isString(error)) {
    return findKey(error, overrideMessages);
  }

  const code = error && error.code;

  if (isString(code)) {
    return findKey(code, overrideMessages);
  }

  return "";
};

const getErrorValue = (error?: string | IErrorField) => {
  if (isUndefined(error) || isString(error)) {
    return undefined;
  }

  const value = error && error.value;

  if (isString(value) || isNumber(value)) {
    return { value };
  }

  return undefined;
};

export const FormError: React.FC<IErrorFieldTextProps> = React.memo(({ error, overrideMessages, ...restProps }) => {
  const { t } = useTranslation("common");
  const message = getMessage(error, overrideMessages);
  const valueObj = getErrorValue(error);

  if (!message) {
    return null;
  }

  return <IrisFormError text={t(message, valueObj)} {...restProps} />;
});
